/**
 * Hubs, Squads and Hub tasks — Tasks module, Epic 1 (US 1.1–1.8).
 *
 * Backed by services/calendar (`/api/calendar/hubs/…`), the same service that
 * owns personal tasks. Hub tasks never appear in the personal "My Tasks"
 * list; they are read here, per Hub, from the one task store both the Hub
 * board and the Squad views read (US 1.7).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_CALENDAR_PREFIX, SuiteApiClient, SuiteApiError } from "@weco/suite-client";
import type { TaskCard, TaskPatchBody } from "../task/api";

const client = new SuiteApiClient(API_CALENDAR_PREFIX);

export type HubPrivacy = "public" | "private";
export type HubRole = "admin" | "member";
export type BoardStatus = "todo" | "in_progress" | "review" | "done";
export type HubTaskPriority = "urgent" | "high" | "medium" | "low";

export interface HubPerson {
  email: string;
  name: string;
}

export type SquadRole = "admin" | "member";

export interface SquadInfo {
  id: string;
  hub: string;
  name: string;
  description: string;
  color: string;
  member_count: number;
  members: (HubPerson & { role: SquadRole; open_tasks?: number })[];
  my_role: SquadRole | null;
  /** Edit, delete and manage members (US 2.2): Squad admins, Hub admins, the Workspace Owner. */
  can_manage: boolean;
  task_count: number;
  done_count: number;
  open_task_count: number;
  /** US 5.2 — the Squad's own statuses, in order. */
  statuses?: SquadStatus[];
}

export interface SquadStatus {
  id?: string;
  key: string;
  name: string;
  color: string;
  category: "not_started" | "active" | "done" | "closed";
  position?: number;
}

export interface HubInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  privacy: HubPrivacy;
  archived: boolean;
  my_role: HubRole | null;
  is_member: boolean;
  can_admin: boolean;
  favorite: boolean;
  muted: boolean;
  member_count: number;
  squad_count: number;
  task_count: number;
  done_count: number;
  squads: SquadInfo[];
  created_at: string;
  /* Detail only */
  members?: (HubPerson & { role: HubRole; open_tasks?: number })[];
  /** US 3.1 — people outside the Workspace who opened an invite link (admins only). */
  access_requests?: AccessRequest[];
  notifications_default?: boolean;
  auto_assign_default?: boolean;
  /** US 13.6 — Mochi's Find tasks may add tasks to this hub. */
  mochi_find_tasks?: boolean;
  last_activity_at?: string | null;
}

export interface AccessRequest {
  id: string;
  email: string;
  name: string;
  squad: string | null;
  created_at: string;
}

/** A Hub task as boards and lists see it (the full card, Epic 4). */
export type HubTask = TaskCard;

export interface HubActivityRow {
  id: string;
  actor: string;
  text: string;
  at: string;
}

export interface NewHubInput {
  name: string;
  description: string;
  color: string;
  icon: string;
  privacy: HubPrivacy;
  members: { email: string; name: string; role: HubRole }[];
  squads: { name: string; description: string; color: string; members: string[] }[];
}

/** US 2.3 — unread Inbox items for a Squad's tasks, per Squad page tab. */
export interface SquadBadges {
  muted: boolean;
  list: number;
  board: number;
  table: number;
}
export type BadgeTab = "list" | "board" | "table";

/** US 2.1 — squad names are at most 40 characters. */
export const SQUAD_NAME_MAX = 40;

/** A Members panel is for a Hub, or for one of its Squads. */
export interface MembersTarget {
  hubId: string;
  squadId?: string | null;
}

export type HubTaskPatch = TaskPatchBody & {
  /** Offline edits only: the version the change was made against (US 3.4). */
  base_updated_at?: string;
};

/** The address an invite link opens (US 3.1). */
export const inviteUrl = (token: string) => `${window.location.origin}/tasks/join/${token}`;

export const BOARD_STATUSES: BoardStatus[] = ["todo", "in_progress", "review", "done"];

/** The design's HUB_COLORS ramp (DS status ramps + neutrals). */
export const HUB_COLORS = ["#1686F2", "#0E4A83", "#19CE70", "#F59E0B", "#FB2C36", "#686868"] as const;
/** Lucide icon choices for a Hub (design: tasks-wizard.jsx HUB_ICONS). */
export const HUB_ICONS = [
  "Folder", "Rocket", "Palette", "Code", "Megaphone", "Users",
  "Briefcase", "Sparkles", "Target", "Flag", "Wrench", "Globe",
] as const;

/** An API error with the server's own message and code, so the UI can show them. */
export class HubApiError extends Error {
  code: string;
  /** On a 409 conflict: who saved the task in the meantime. */
  updatedBy?: string;
  constructor(message: string, code = "") {
    super(message);
    this.code = code;
  }
}

async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    // No HTTP response at all: offline, or the server couldn't be reached.
    if (!(err instanceof SuiteApiError)) throw new HubApiError("You're offline.", "network");
    const e = err as { details?: { detail?: string; code?: string; updated_by?: string }; message?: string };
    const out = new HubApiError(e.details?.detail || e.message || "Request failed", e.details?.code || "");
    out.updatedBy = e.details?.updated_by;
    throw out;
  }
}

export const hubsApi = {
  list: (archived = false) =>
    call(() => client.get<{ results: HubInfo[] }>(`/hubs/${archived ? "?archived=1" : ""}`)).then((r) => r.results),
  get: (id: string) => call(() => client.get<HubInfo>(`/hubs/${id}/`)),
  create: (input: NewHubInput) => call(() => client.post<HubInfo>("/hubs/", input)),
  update: (id: string, patch: Partial<Pick<HubInfo, "name" | "description" | "icon" | "color" | "privacy" | "notifications_default" | "auto_assign_default" | "mochi_find_tasks">>) =>
    call(() => client.patch<HubInfo>(`/hubs/${id}/`, patch)),
  remove: (id: string, confirmName: string) =>
    call(() => client.delete<{ squads: number; tasks: number }>(`/hubs/${id}/?confirm_name=${encodeURIComponent(confirmName)}`)),
  action: (id: string, action: "duplicate" | "archive" | "restore" | "favorite" | "unfavorite" | "mute" | "unmute" | "leave") =>
    call(() => client.post<HubInfo & { left?: boolean; private?: boolean }>(`/hubs/${id}/${action}/`, {})),
  addMembers: (id: string, members: { email: string; name: string; role?: HubRole }[]) =>
    call(() => client.post<HubInfo>(`/hubs/${id}/members/`, { members })),
  setRole: (id: string, email: string, role: HubRole) =>
    call(() => client.patch<HubInfo>(`/hubs/${id}/members/${encodeURIComponent(email)}/`, { role })),
  removeMember: (id: string, email: string) =>
    call(() => client.delete<HubInfo>(`/hubs/${id}/members/${encodeURIComponent(email)}/`)),
  addSquad: (id: string, squad: { name: string; description?: string; color?: string; members?: string[] }) =>
    call(() => client.post<HubInfo & { squad: SquadInfo }>(`/hubs/${id}/squads/`, squad)),
  updateSquad: (squadId: string, patch: { name?: string; description?: string; color?: string }) =>
    call(() => client.patch<HubInfo & { squad: SquadInfo }>(`/squads/${squadId}/`, patch)),
  removeSquad: (squadId: string) => call(() => client.delete<HubInfo & { kept_tasks: number }>(`/squads/${squadId}/`)),
  addSquadMembers: (squadId: string, members: string[]) =>
    call(() => client.post<HubInfo & { squad: SquadInfo }>(`/squads/${squadId}/members/`, { members })),
  setSquadRole: (squadId: string, email: string, role: SquadRole) =>
    call(() => client.patch<HubInfo & { squad: SquadInfo }>(`/squads/${squadId}/members/${encodeURIComponent(email)}/`, { role })),
  removeSquadMember: (squadId: string, email: string) =>
    call(() => client.delete<HubInfo & { squad: SquadInfo }>(`/squads/${squadId}/members/${encodeURIComponent(email)}/`)),
  squadBadges: (squadId: string) => call(() => client.get<SquadBadges>(`/squads/${squadId}/badges/`)),
  markTabSeen: (squadId: string, tab: BadgeTab) => call(() => client.post<SquadBadges>(`/squads/${squadId}/badges/`, { tab })),
  tasks: (id: string, archived = false) =>
    call(() => client.get<{ results: HubTask[] }>(`/hubs/${id}/tasks/${archived ? "?archived=1" : ""}`)).then((r) => r.results),
  createTask: (id: string, task: Partial<Omit<HubTask, "assignees">> & { title: string; squad: string; assignees?: string[] }) =>
    call(() => client.post<HubTask>(`/hubs/${id}/tasks/`, task)),
  updateTask: (taskId: string, patch: HubTaskPatch) => call(() => client.patch<HubTask>(`/hub-tasks/${taskId}/`, patch)),
  // ── Epic 3 ──
  invitable: (target: MembersTarget, q = "") =>
    call(() =>
      client.get<{ results: HubPerson[] }>(
        `${target.squadId ? `/squads/${target.squadId}` : `/hubs/${target.hubId}`}/invitable/${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      ),
    ).then((r) => r.results),
  inviteLink: (target: MembersTarget) =>
    call(() => client.get<{ token: string }>(`${target.squadId ? `/squads/${target.squadId}` : `/hubs/${target.hubId}`}/invite-link/`)).then((r) => r.token),
  join: (token: string) => call(() => client.post<{ hub: string; squad: string | null; joined: boolean }>("/tasks-join/", { token })),
  requestAccess: (token: string) => call(() => client.post<{ requested: boolean }>("/tasks-join/request/", { token })),
  dismissRequest: (requestId: string) => call(() => client.delete<HubInfo>(`/tasks-access-requests/${requestId}/`)),
  removeTask: (taskId: string) => call(() => client.delete<void>(`/hub-tasks/${taskId}/`)),
  activity: (id: string) =>
    call(() => client.get<{ results: HubActivityRow[] }>(`/hubs/${id}/activity/`)).then((r) => r.results),
};

export const HUBS_KEY = ["tasks", "hubs"] as const;
const hubKey = (id: string) => ["tasks", "hub", id] as const;
const hubTasksKey = (id: string) => ["tasks", "hub", id, "tasks"] as const;

export function useHubs(archived = false) {
  return useQuery({ queryKey: [...HUBS_KEY, archived ? "archived" : "active"], queryFn: () => hubsApi.list(archived) });
}

export function useHub(id: string | null) {
  return useQuery({ queryKey: hubKey(id ?? ""), queryFn: () => hubsApi.get(id!), enabled: !!id, retry: false });
}

export function useHubTasks(id: string | null, archived = false) {
  return useQuery({
    queryKey: archived ? [...hubTasksKey(id ?? ""), "archived"] : hubTasksKey(id ?? ""),
    queryFn: () => hubsApi.tasks(id!, archived),
    enabled: !!id,
  });
}

export function useHubActivity(id: string | null) {
  return useQuery({ queryKey: ["tasks", "hub", id ?? "", "activity"], queryFn: () => hubsApi.activity(id!), enabled: !!id });
}

export const squadBadgesKey = (squadId: string) => ["tasks", "hub", "squad-badges", squadId] as const;

/** Squads deleted in this session: nothing asks the server about them again
 *  (the Squad page is still mounted for a moment after its Squad is gone). */
const deletedSquads = new Set<string>();
export const forgetSquad = (squadId: string) => deletedSquads.add(squadId);
export const isDeletedSquad = (squadId: string) => deletedSquads.has(squadId);

export function useSquadBadges(squadId: string | null) {
  return useQuery({
    queryKey: squadBadgesKey(squadId ?? ""),
    // Checked at fetch time too: a refresh right after a delete can still
    // reach this query before the page re-renders without the Squad.
    queryFn: () =>
      deletedSquads.has(squadId!) ? Promise.resolve<SquadBadges>({ muted: false, list: 0, board: 0, table: 0 }) : hubsApi.squadBadges(squadId!),
    enabled: !!squadId && !deletedSquads.has(squadId),
    retry: false,
    // New status changes from teammates show up without a reload.
    refetchInterval: 60_000,
  });
}

/** Refresh every Hub surface — the tree, lists, the Hub page and its tasks —
 *  so a rename or move shows everywhere without a reload (US 1.4). */
export function useRefreshHubs() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: HUBS_KEY });
    void qc.invalidateQueries({ queryKey: ["tasks", "hub"] });
  };
}

export function useHubMutation<A, R>(fn: (args: A) => Promise<R>) {
  const refresh = useRefreshHubs();
  return useMutation({ mutationFn: fn, onSuccess: refresh });
}
